/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    Checkbox,
    Radio,
    RadioGroup,
    VStack,
    Text,
    Box,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Textarea} from '@chakra-ui/react'
import {VisibilityIcon, VisibilityOffIcon, CalendarIcon} from '../../components/icons'
import {useIntl} from 'react-intl'
import {sendViewErrorEvent} from '../../utils/gtm-utils'

const Field = ({
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    labelProps,
    control,
    autoComplete,
    defaultValue,
    helpText,
    children,
    inputRef,
    hideLabel = false,
    isResetPassword = false,
    formControlProps,
    variant,
    rows = 4,
    maxLength,
    showCharacterCount = false,
    showPasswordToggle = true,
    onClick,
    isLoginPage = false
}) => {
    const intl = useIntl()
    const [hidePassword, setHidePassword] = useState(true)
    const [isDateFocused, setIsDateFocused] = useState(false)
    const [shouldOpenPicker, setShouldOpenPicker] = useState(false)
    const dateInputRef = useRef(null)
    const PasswordIcon = hidePassword ? VisibilityIcon : VisibilityOffIcon
    const passwordIconLabel = hidePassword
        ? intl.formatMessage({
              id: 'field.password.assistive_msg.show_password',
              defaultMessage: 'Show password'
          })
        : intl.formatMessage({
              id: 'field.password.assistive_msg.hide_password',
              defaultMessage: 'Hide password'
          })

    const finalPlaceholder =
        variant === 'floating' || variant === 'floatingProfileForm' ? ' ' : placeholder

    useEffect(() => {
        if (error) sendViewErrorEvent(error.message)
    }, [error?.message])

    useEffect(() => {
        if (shouldOpenPicker && isDateFocused) {
            // Wait for the input to re-render as type="date" and ref to update
            const attemptOpenPicker = () => {
                if (dateInputRef.current && dateInputRef.current.type === 'date') {
                    try {
                        dateInputRef.current.showPicker?.()
                    } catch (error) {
                        console.log('showPicker not supported')
                    }
                    setShouldOpenPicker(false)
                } else {
                    // Ref not ready yet, try again
                    requestAnimationFrame(attemptOpenPicker)
                }
            }

            requestAnimationFrame(attemptOpenPicker)
        }
    }, [shouldOpenPicker, isDateFocused])

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, value, ref}}) => {
                const getInputType = () => {
                    if (type === 'password') {
                        return hidePassword ? 'password' : 'text'
                    }
                    if (type === 'date' && !isDateFocused && !value) {
                        return 'text'
                    }
                    return type
                }

                const inputType = getInputType()

                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps

                const labelElement =
                    !hideLabel &&
                    !['checkbox', 'radio', 'hidden'].includes(type) &&
                    (formLabel || <FormLabel {...labelProps}>{label}</FormLabel>)

                return (
                    <FormControl
                        id={name}
                        isInvalid={error}
                        variant={variant}
                        {...formControlProps}
                    >
                        {variant !== 'floating' &&
                            variant !== 'floatingProfileForm' &&
                            labelElement}

                        <InputGroup>
                            {[
                                'text',
                                'password',
                                'email',
                                'phone',
                                'tel',
                                'number',
                                'date'
                            ].includes(type) && (
                                <Input
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                        if (type === 'date') {
                                            dateInputRef.current = node
                                        }
                                    }}
                                    onChange={onChange}
                                    onClick={(e) => {
                                        if (type === 'date' && !isDateFocused) {
                                            e.preventDefault()
                                            const scrollY = window.scrollY
                                            setIsDateFocused(true)
                                            // Restore scroll position after state change
                                            requestAnimationFrame(() => {
                                                window.scrollTo(0, scrollY)
                                                if (dateInputRef.current) {
                                                    dateInputRef.current.focus({
                                                        preventScroll: true
                                                    })
                                                }
                                            })
                                        }
                                        onClick?.(e)
                                    }}
                                    onFocus={(e) => {
                                        if (type === 'date' && !isDateFocused) {
                                            const scrollY = window.scrollY
                                            setIsDateFocused(true)
                                            // Restore scroll position after state change
                                            requestAnimationFrame(() => {
                                                window.scrollTo(0, scrollY)
                                            })
                                        }
                                        _inputProps?.onFocus?.(e)
                                    }}
                                    onBlur={(e) => {
                                        if (type === 'date') {
                                            setIsDateFocused(false)
                                        }
                                        _inputProps?.onBlur?.(e)
                                    }}
                                    value={value}
                                    type={inputType}
                                    placeholder={finalPlaceholder}
                                    autoComplete={autoComplete}
                                    borderColor="gray.700"
                                    {..._inputProps}
                                    fontSize={!isLoginPage ? {base: '16px', lg: '14px'} : _inputProps?.fontSize}
                                    sx={{
                                        ..._inputProps?.sx,
                                        ...(type === 'date' && !isDateFocused && !value
                                            ? {
                                                  '&::-webkit-calendar-picker-indicator': {
                                                      display: 'none !important',
                                                      opacity: '0 !important',
                                                      width: '0 !important',
                                                      height: '0 !important'
                                                  }
                                              }
                                            : {})
                                    }}
                                />
                            )}

                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value || ''}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {type === 'password' && showPasswordToggle && (
                                <InputRightElement w="auto">
                                    <IconButton
                                        size="xs"
                                        variant="ghosted"
                                        aria-label={passwordIconLabel}
                                        icon={<PasswordIcon color="black" w={4} h={4} />}
                                        onClick={() => setHidePassword(!hidePassword)}
                                    />
                                </InputRightElement>
                            )}

                            {type === 'date' && !isDateFocused && !value && (
                                <InputRightElement w="auto">
                                    <IconButton
                                        size="xs"
                                        variant="ghosted"
                                        aria-label={intl.formatMessage({
                                            id: 'field.calendar.assistive_msg.open_calendar',
                                            defaultMessage: 'Open calendar'
                                        })}
                                        icon={<CalendarIcon color="black" w={4} h={4} mr="-4px" />}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            const scrollY = window.scrollY

                                            // Set states - this will trigger useEffect
                                            setIsDateFocused(true)
                                            setShouldOpenPicker(true)

                                            // Restore scroll and focus
                                            requestAnimationFrame(() => {
                                                window.scrollTo(0, scrollY)
                                                if (dateInputRef.current) {
                                                    dateInputRef.current.focus({
                                                        preventScroll: true
                                                    })
                                                }
                                            })
                                        }}
                                    />
                                </InputRightElement>
                            )}

                            {type === 'select' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    value={value || ''}
                                    placeholder={finalPlaceholder}
                                    {..._inputProps}
                                >
                                    {options.map((opt) => (
                                        <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'radio' && (
                                <RadioGroup
                                    ref={ref}
                                    onChange={onChange}
                                    value={value || ''}
                                    name={name}
                                    {..._inputProps}
                                >
                                    <VStack align="start" spacing={3}>
                                        {options.map((opt) => (
                                            <Radio
                                                key={`${opt.label}-${opt.value}`}
                                                value={opt.value}
                                                size="lg"
                                            >
                                                <Text ml={2} fontSize="md" {...labelProps}>
                                                    {opt.label}
                                                </Text>
                                            </Radio>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    fontSize="5px"
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                    sx={{
                                        '.chakra-checkbox__control[data-invalid]': {
                                            borderColor: 'black'
                                        }
                                    }}
                                >
                                    <Text {...labelProps} as={'span'}>
                                        {formLabel || label}
                                    </Text>
                                </Checkbox>
                            )}

                            {type === 'textarea' && (
                                <Box width="100%">
                                    <Textarea
                                        ref={ref}
                                        onChange={onChange}
                                        value={value || ''}
                                        placeholder={finalPlaceholder}
                                        rows={rows}
                                        maxLength={maxLength}
                                        border="none"
                                        borderBottom="1px solid"
                                        borderBottomColor="gray.500"
                                        borderRadius="0"
                                        _focusVisible={{
                                            boxShadow: 'none',
                                            borderColor: 'gray.500'
                                        }}
                                        resize="none"
                                        px={0}
                                        {..._inputProps}
                                    />
                                    {showCharacterCount && maxLength && (
                                        <Flex justify="flex-start" mt={1}>
                                            <Text
                                                fontSize="xs"
                                                color={
                                                    value?.length >= maxLength
                                                        ? 'dangerText'
                                                        : 'black'
                                                }
                                            >
                                                {value?.length || 0}/{maxLength}
                                            </Text>
                                        </Flex>
                                    )}
                                </Box>
                            )}

                            {children}
                        </InputGroup>

                        {(variant === 'floating' || variant === 'floatingProfileForm') &&
                            labelElement}

                        {error && type !== 'hidden' && (
                            <FormErrorMessage
                                ml={type === 'checkbox' ? '27px' : '0'}
                                color="dangerText"
                                fontSize={isResetPassword ? 'sm' : 'xs'}
                                lineHeight={isResetPassword ? '18px' : '15px'}
                            >
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'radio',
        'hidden',
        'textarea'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    labelProps: PropTypes.object,
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    children: PropTypes.any,
    inputRef: PropTypes.object,
    hideLabel: PropTypes.bool,
    formControlProps: PropTypes.object,
    variant: PropTypes.string,
    rows: PropTypes.number,
    maxLength: PropTypes.number,
    showCharacterCount: PropTypes.bool,
    showPasswordToggle: PropTypes.bool,
    onClick: PropTypes.func,
    isResetPassword: PropTypes.bool,
    isLoginPage: PropTypes.bool
}

export default Field
